import moment from "moment";
import { validateCpf } from "src/validators/validateCpf";
import { validateCnpj, validateCpfOuCnpj } from "src/validators/validateCnpj";
import {
  validateEstruturaChaveAcesso,
  validateModeloChaveAcesso,
  validateDigitoVerificadorChaveAcesso,
} from "src/validators/validateChaveAcesso";
import { validatePlaca } from "src/validators/validatePlaca";
import yup from "src/validators/yup.Module";
import {
  ETipoValePedagioMDFe,
  EIndicadorPagamentoBancarioMDFe,
  tipoValePedagioMDFeOptions,
  indicadorPagamentoBancarioMDFeOptions,
  ETipoProprietarioMDFe,
  ETipoTransportadorMDFe,
  STORAGEMDFE_KEY,
  getLocal,
  MDFeInput,
  validarMunicipioPertenceUf,
} from "../MDFe.Dto";

export const IdentificacaoSchema = yup.object().shape({
  tipoEmitente: yup
    .number()
    .required("Tipo de emitente deve ser válido.")
    .typeError("Tipo de emitente deve ser válido.")
    .oneOf([1, 2, 3], "Tipo de emitente deve ser válido."),
  tipoTransportador: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? null : val)),
  modal: yup
    .number()
    .required("Modal deve ser válido.")
    .typeError("Modal deve ser válido.")
    .oneOf([1, 2, 3, 4], "Modal deve ser válido."),
  ufInicio: yup
    .string()
    .required("UF de início do carregamento é obrigatória. Selecione a UF onde a carga será carregada.")
    .matches(/^[A-Z]{2}$/, "UF de início deve conter 2 letras maiúsculas (ex: PR, SP, SC)."),
  ufFim: yup
    .string()
    .required("UF de fim da descarga é obrigatória. Selecione a UF onde a carga será descarregada.")
    .matches(/^[A-Z]{2}$/, "UF final deve conter 2 letras maiúsculas (ex: PR, SP, SC)."),
  rntrc: yup.string().when("tipoEmitente", {
    is: (val: number) => Number(val) === 1,
    then: (schema) =>
      schema
        .required("RNTRC é obrigatório para Prestador de Serviço de Transporte")
        .test(
          "rntrc-8-digitos",
          "RNTRC deve conter exatamente 8 dígitos numéricos.",
          (val) => {
            if (!val) return false;
            const limpo = val.replace(/\D/g, "");
            return limpo.length === 8;
          }
        ),
    otherwise: (schema) =>
      schema
        .nullable()
        .notRequired()
        .test(
          "rntrc-opcional-8-digitos",
          "RNTRC deve conter exatamente 8 dígitos numéricos.",
          (val) => {
            if (!val) return true;
            const limpo = val.replace(/\D/g, "");
            return limpo.length === 0 || limpo.length === 8;
          }
        ),
  }),
  previsaoInicioViagemDataHora: yup.string().nullable().notRequired(),
});

export type IdentificacaoFormType = yup.InferType<typeof IdentificacaoSchema>;

export const UFS_VALIDAS_MDFE = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA",
  "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN",
  "RO", "RR", "RS", "SC", "SE", "SP", "TO", "EX",
];

export const PercursoSchema = yup.object().shape({
  percursos: yup
    .array()
    .of(
      yup.object().shape({
        ufPercurso: yup
          .string()
          .required("UF do percurso é obrigatória.")
          .test(
            "uf-percurso-tamanho",
            "UF do percurso deve conter 2 caracteres.",
            (val) => !val || val.trim().length === 2
          )
          .test(
            "uf-percurso-valida",
            "UF do percurso é inválida.",
            (val) => Boolean(val && UFS_VALIDAS_MDFE.includes(val.trim().toUpperCase()))
          ),
        sequencia: yup
          .number()
          .required("Sequência do percurso deve ser maior que zero.")
          .typeError("Sequência deve ser numérica.")
          .min(1, "Sequência do percurso deve ser maior que zero."),
      })
    )
    .notRequired(),
});

export type PercursoFormType = yup.InferType<typeof PercursoSchema>;

export const ProprietarioVeiculoSchema = yup
  .object()
  .shape({
    documento: yup
      .string()
      .nullable()
      .notRequired()
      .transform((v) => (v ? v.replace(/[.\-/\s]/g, "").toUpperCase() : ""))
      .test(
        "doc-proprietario-valido",
        "Documento (CPF ou CNPJ) do proprietário inválido.",
        (v) => !v || validateCpfOuCnpj(v)
      ),
    cpf: yup
      .string()
      .nullable()
      .notRequired()
      .transform((v) => (v ? v.replace(/\D/g, "") : ""))
      .test(
        "cpf-proprietario-valido",
        "CPF do proprietário inválido.",
        (v) => !v || validateCpf(v)
      ),
    cnpj: yup
      .string()
      .nullable()
      .notRequired()
      .transform((v) => (v ? v.replace(/[^A-Za-z0-9]/g, "").toUpperCase() : ""))
      .test(
        "cnpj-proprietario-valido",
        "CNPJ do proprietário inválido.",
        (v) => !v || validateCnpj(v)
      ),
    rntrc: yup
      .string()
      .required("RNTRC do proprietário é obrigatório.")
      .transform((v) => (v ? v.replace(/\D/g, "") : ""))
      .test(
        "rntrc-proprietario-8-digitos",
        "RNTRC do proprietário deve conter 8 dígitos.",
        (v) => Boolean(v && v.length === 8)
      )
      .test(
        "rntrc-proprietario-apenas-numeros",
        "RNTRC do proprietário deve conter apenas dígitos numéricos.",
        (v) => Boolean(v && /^\d+$/.test(v))
      ),
    nome: yup
      .string()
      .required("Nome do proprietário é obrigatório.")
      .min(2, "Nome do proprietário deve ter entre 2 e 60 caracteres.")
      .max(60, "Nome do proprietário deve ter entre 2 e 60 caracteres."),
    ie: yup
      .string()
      .nullable()
      .notRequired()
      .transform((v) => (v ? v.trim() : ""))
      .test(
        "ie-proprietario-len",
        "Inscrição Estadual do proprietário deve ter entre 2 e 14 caracteres.",
        (v) => !v || (v.length >= 2 && v.length <= 14)
      ),
    uf: yup
      .string()
      .required("UF do proprietário é obrigatória.")
      .test(
        "uf-proprietario-len",
        "UF do proprietário deve conter 2 caracteres.",
        (v) => !v || v.trim().length === 2
      )
      .test(
        "uf-proprietario-valida",
        "UF do proprietário é inválida.",
        (v) => Boolean(v && UFS_VALIDAS_MDFE.includes(v.trim().toUpperCase()))
      ),
    tipoProprietario: yup
      .number()
      .required("Tipo de proprietário deve ser válido.")
      .typeError("Tipo de proprietário deve ser válido.")
      .oneOf([0, 1, 2], "Tipo de proprietário deve ser válido."),
  })
  .test(
    "proprietario-doc-obrigatorio",
    "Deve ser informado o documento (CPF ou CNPJ) do proprietário do veículo.",
    function (val) {
      if (!val) return true;
      const doc = val.documento || val.cpf || val.cnpj;
      return Boolean(doc && doc.trim() !== "");
    }
  );

export type ProprietarioVeiculoFormType = yup.InferType<typeof ProprietarioVeiculoSchema>;

export const VeiculoSchema = yup.object().shape({
  placaVeiculoTracao: yup
    .string()
    .required("Placa do veículo de tração é obrigatória.")
    .transform((val) => (val ? val.replace(/[^A-Za-z0-9]/g, "").toUpperCase() : val))
    .test("placa-valida", "Placa do veículo de tração deve conter 7 caracteres válidos.", (val) =>
      Boolean(val && validatePlaca(val))
    ),
  ufVeiculoTracao: yup
    .string()
    .required("UF do veículo de tração é obrigatória.")
    .matches(/^[A-Z]{2}$/, "UF do veículo deve conter 2 caracteres."),
  renavamVeiculoTracao: yup
    .string()
    .nullable()
    .notRequired()
    .test(
      "renavam-valido",
      "RENAVAM deve conter entre 9 e 11 dígitos numéricos.",
      (v) => {
        if (!v) return true;
        const limpo = v.replace(/\D/g, "");
        return limpo.length >= 9 && limpo.length <= 11;
      }
    ),
  taraVeiculoTracao: yup
    .number()
    .required("Tara do veículo de tração deve ser maior que zero.")
    .typeError("Tara deve ser numérica")
    .min(1, "Tara do veículo de tração deve ser maior ou igual a 1.")
    .max(999999, "Tara do veículo de tração deve ter no máximo 6 dígitos (até 999999)."),
  capacidadeKGVeiculoTracao: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? null : val)),
  capacidadeM3VeiculoTracao: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? null : val)),
  tipoRodadoVeiculoTracao: yup
    .number()
    .required("Tipo de rodado deve ser válido.")
    .typeError("Tipo de rodado deve ser válido.")
    .min(1, "Tipo de rodado deve ser válido.")
    .max(6, "Tipo de rodado deve ser válido."),
  tipoCarroceriaVeiculoTracao: yup
    .number()
    .required("Tipo de carroceria deve ser válido.")
    .typeError("Tipo de carroceria deve ser válido.")
    .min(0, "Tipo de carroceria deve ser válido.")
    .max(5, "Tipo de carroceria deve ser válido."),
  condutores: yup
    .array()
    .of(
      yup.object().shape({
        nomeCondutor: yup
          .string()
          .required("Nome do condutor é obrigatório.")
          .min(2, "Nome do condutor deve ter entre 2 e 60 caracteres.")
          .max(60, "Nome do condutor deve ter entre 2 e 60 caracteres."),
        cpfCondutor: yup
          .string()
          .required("CPF do condutor é obrigatório.")
          .transform((v) => (v ? v.replace(/\D/g, "") : ""))
          .test(
            "cpf-condutor-valido",
            "CPF do condutor é inválido.",
            (v) => validateCpf(v)
          ),
      })
    )
    .required("Deve haver pelo menos um condutor informado.")
    .min(1, "Deve haver pelo menos um condutor informado.")
    .max(10, "Máximo de 10 condutores permitidos"),
  reboques: yup
    .array()
    .of(
      yup.object().shape({
        codigoInterno: yup
          .string()
          .nullable()
          .notRequired()
          .max(10, "Código interno deve conter no máximo 10 caracteres."),
        placa: yup
          .string()
          .required("Placa deve conter 7 caracteres.")
          .transform((val) => (val ? val.replace(/[^A-Za-z0-9]/g, "").toUpperCase() : val))
          .test("placa-reboque", "Placa do reboque deve conter 7 caracteres válidos.", (val) =>
            Boolean(val && validatePlaca(val))
          ),
        uf: yup
          .string()
          .required("UF deve conter 2 caracteres.")
          .matches(/^[A-Z]{2}$/, "UF deve conter 2 caracteres."),
        tara: yup
          .number()
          .required("Tara do reboque deve ter entre 1 e 6 dígitos.")
          .typeError("Tara deve ser numérica")
          .min(1, "Tara do reboque deve ser maior ou igual a 1.")
          .max(999999, "Tara do reboque deve ter no máximo 6 dígitos (até 999999)."),
        capacidadeKG: yup
          .number()
          .required("Capacidade em KG do reboque é obrigatória.")
          .typeError("Capacidade em KG deve ser numérica")
          .min(1, "Capacidade em KG do reboque deve ser maior ou igual a 1.")
          .max(999999, "Capacidade em KG do reboque deve ter no máximo 6 dígitos (até 999999)."),
        capacidadeM3: yup
          .number()
          .nullable()
          .notRequired()
          .transform((val, orig) => (orig === "" || isNaN(val) ? null : val)),
        tipoCarroceria: yup
          .number()
          .required("Tipo de carroceria deve ser válido.")
          .typeError("Tipo de carroceria deve ser válido.")
          .min(0, "Tipo de carroceria deve ser válido.")
          .max(5, "Tipo de carroceria deve ser válido."),
        renavam: yup
          .string()
          .nullable()
          .notRequired()
          .test(
            "renavam-reboque",
            "RENAVAM deve conter entre 9 e 11 dígitos numéricos.",
            (v) => {
              if (!v) return true;
              const limpo = v.replace(/\D/g, "");
              return limpo.length >= 9 && limpo.length <= 11;
            }
          ),
      })
    )
    .max(3, "Máximo de 3 reboques permitidos")
    .notRequired(),
  tipoTransportador: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
    .oneOf([1, 2, 3], "Tipo de transportador deve ser válido.")
    .test(
      "rejeicao-745-veiculo",
      "O tipo de transportador não pode ser informado quando não estiver informado proprietário do veículo de tração.",
      function (tipoTransp) {
        if (!tipoTransp) return true;
        const prop = this.parent?.proprietarioVeiculoTracao;
        return Boolean(
          prop && (prop.documento || prop.cpf || prop.cnpj || prop.nome)
        );
      }
    )
    .test(
      "rejeicao-744-veiculo",
      "Para proprietário pessoa jurídica (CNPJ), o tipo de transportador deve ser ETC ou CTC.",
      function (tipoTransp) {
        if (!tipoTransp) return true;
        const prop = this.parent?.proprietarioVeiculoTracao;
        const docLimpo = prop?.documento
          ? prop.documento.replace(/[.\-/\s]/g, "")
          : (prop?.cnpj || "");
        if (docLimpo && docLimpo.trim().length === 14) {
          return Number(tipoTransp) === 1 || Number(tipoTransp) === 3;
        }
        return true;
      }
    ),
  proprietarioVeiculoTracao: ProprietarioVeiculoSchema
    .nullable()
    .notRequired()
    .default(undefined)
    .transform((val) => {
      if (!val) return undefined;
      const hasAnyField = Boolean(
        val.documento || val.cpf || val.cnpj || val.nome || val.rntrc || val.ie
      );
      return hasAnyField ? val : undefined;
    }),
});

export type VeiculoFormType = yup.InferType<typeof VeiculoSchema>;

export const CarregamentoSchema = yup.object().shape({
  municipiosCarregamento: yup
    .array()
    .of(
      yup.object().shape({
        codigoMunicipioCarregamento: yup
          .number()
          .required("Código do município de carregamento deve ser um código IBGE válido de 7 dígitos.")
          .typeError("Código do município de carregamento deve ser um código IBGE válido de 7 dígitos.")
          .min(1000000, "Código do município de carregamento deve ser um código IBGE válido de 7 dígitos.")
          .max(9999999, "Código do município de carregamento deve ser um código IBGE válido de 7 dígitos.")
          .test(
            "rejeicao-456-uf-carregamento",
            function (val) {
              if (!val) return true;
              const mdfe = getLocal<MDFeInput>(STORAGEMDFE_KEY);
              const ufInicio = mdfe?.ufInicio;
              if (!ufInicio) return true;
              const valido = validarMunicipioPertenceUf(val, ufInicio);
              if (!valido) {
                return this.createError({
                  message: `Código de Município (${val}) diverge da UF de carregamento do MDF-e (${ufInicio}) (Rejeição 456).`,
                });
              }
              return true;
            }
          ),
        nomeMunicipioCarregamento: yup
          .string()
          .required("Nome do município de carregamento é obrigatório.")
          .min(2, "Nome do município de carregamento deve ter entre 2 e 60 caracteres.")
          .max(60, "Nome do município de carregamento deve ter entre 2 e 60 caracteres."),
      })
    )
    .required("Deve haver pelo menos um município de carregamento.")
    .min(1, "Deve haver pelo menos um município de carregamento."),
});

export type CarregamentoFormType = yup.InferType<typeof CarregamentoSchema>;

export const DocumentosSchema = yup.object().shape({
  municipiosDescarga: yup
    .array()
    .of(
      yup.object().shape({
        codigoMunicipioDescarga: yup
          .number()
          .required("Código do município de descarregamento deve ser um código IBGE válido de 7 dígitos.")
          .typeError("Código do município de descarregamento deve ser um código IBGE válido de 7 dígitos.")
          .min(1000000, "Código do município de descarregamento deve ser um código IBGE válido de 7 dígitos.")
          .max(9999999, "Código do município de descarregamento deve ser um código IBGE válido de 7 dígitos.")
          .test(
            "rejeicao-612-uf-descarga",
            function (val) {
              if (!val) return true;
              const mdfe = getLocal<MDFeInput>(STORAGEMDFE_KEY);
              const ufFim = mdfe?.ufFim;
              if (!ufFim) return true;
              const valido = validarMunicipioPertenceUf(val, ufFim);
              if (!valido) {
                return this.createError({
                  message: `Código de Município (${val}) diverge da UF de descarga do MDF-e (${ufFim}) (Rejeição 612).`,
                });
              }
              return true;
            }
          ),
        nomeMunicipioDescarga: yup
          .string()
          .required("Nome do município de descarregamento é obrigatório.")
          .min(2, "Nome do município de descarregamento deve ter entre 2 e 60 caracteres.")
          .max(60, "Nome do município de descarregamento deve ter entre 2 e 60 caracteres."),
        documentos: yup
          .array()
          .of(
            yup.object().shape({
              chaveAcesso: yup
                .string()
                .required("Chave de acesso é obrigatória.")
                .transform((v) => (v ? v.replace(/[\s.-]/g, "").toUpperCase() : ""))
                .test(
                  "chave-44-digitos",
                  "Chave de acesso deve conter exatamente 44 dígitos.",
                  (val) => Boolean(val && val.length === 44)
                )
                .test(
                  "chave-estrutura",
                  "Chave de acesso deve conter apenas dígitos numéricos.",
                  (val) => !val || validateEstruturaChaveAcesso(val)
                )
                .test(
                  "chave-modelo",
                  "Modelo da chave de acesso incompatível (deve ser 57 para CT-e ou 55 para NF-e).",
                  function (val) {
                    if (!val || val.length !== 44) return true;
                    const tipoDocumento = this.parent?.tipoDocumento;
                    return validateModeloChaveAcesso(val, Number(tipoDocumento));
                  }
                )
                .test(
                  "chave-dv",
                  "Dígito verificador da chave de acesso é inválido.",
                  (val) => !val || val.length !== 44 || validateDigitoVerificadorChaveAcesso(val)
                ),
              tipoDocumento: yup
                .number()
                .required("Tipo de documento é obrigatório")
                .oneOf([1, 2], "Tipo de documento deve ser 1 (CT-e) ou 2 (NF-e)"),
              segundoCodigoBarras: yup
                .string()
                .nullable()
                .notRequired()
                .max(36, "Segundo código de barras deve conter no máximo 36 caracteres."),
              indicadorReentrega: yup.boolean().notRequired(),
            })
          )
          .required("Cada município de descarga deve conter pelo menos um documento fiscal (CT-e ou NF-e).")
          .min(1, "Cada município de descarga deve conter pelo menos um documento fiscal (CT-e ou NF-e)."),
      })
    )
    .required("Deve haver pelo menos um município de descarregamento.")
    .min(1, "Deve haver pelo menos um município de descarregamento."),
});

export type DocumentosFormType = yup.InferType<typeof DocumentosSchema>;

export const TotaisSchema = yup.object().shape({
  valorTotalCarga: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
    .min(0, "Valor total da carga deve ser maior ou igual a zero."),
  pesoBrutoTotal: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
    .min(0, "Peso bruto total deve ser maior ou igual a zero."),
  unidadeMedidaPeso: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? 1 : val)),
  seguros: yup
    .array()
    .of(
      yup.object().shape({
        responsavelSeguro: yup
          .number()
          .required("Responsável pelo seguro deve ser válido (1 = Emitente do MDF-e, 2 = Responsável pela contratação do serviço).")
          .oneOf([1, 2], "Responsável pelo seguro deve ser válido (1 = Emitente do MDF-e, 2 = Responsável pela contratação do serviço)."),
        documentoResponsavel: yup
          .string()
          .nullable()
          .notRequired()
          .test(
            "doc-responsavel-obrigatorio-contratante",
            "Documento (CPF ou CNPJ) do responsável pelo seguro é obrigatório quando o contratante for o responsável (Rejeição 699).",
            function (v) {
              const resp = Number(this.parent?.responsavelSeguro);
              if (resp === 2) {
                return Boolean(v && v.trim() !== "");
              }
              return true;
            }
          )
          .test(
            "doc-responsavel-valido",
            "Documento do responsável pelo seguro é inválido (deve ser um CPF ou CNPJ válido).",
            (v) => {
              if (!v || v.trim() === "") return true;
              return validateCpfOuCnpj(v);
            }
          ),
        nomeSeguradora: yup
          .string()
          .required("Nome da seguradora é obrigatório (Rejeição 699).")
          .min(2, "Nome da seguradora deve conter entre 2 e 30 caracteres conforme manual SEFAZ.")
          .max(30, "Nome da seguradora deve conter no máximo 30 caracteres conforme manual SEFAZ."),
        cnpjSeguradora: yup
          .string()
          .required("CNPJ da seguradora é obrigatório (Rejeição 699).")
          .transform((v) =>
            v ? v.replace(/[^A-Za-z0-9]/g, "").toUpperCase() : ""
          )
          .test(
            "cnpj-seguradora-valido",
            "CNPJ da seguradora é inválido.",
            (v) => !v || validateCnpj(v)
          ),
        numeroApolice: yup
          .string()
          .required("Número da apólice do seguro é obrigatório para o modal rodoviário (Rejeição 699).")
          .max(20, "Número da apólice deve conter no máximo 20 caracteres conforme manual SEFAZ."),
        numeroAverbaCorrente: yup
          .string()
          .required("Número de averbação do seguro é obrigatório para o modal rodoviário (Rejeição 699).")
          .max(40, "Número de averbação deve conter no máximo 40 caracteres conforme manual SEFAZ."),
      })
    )
    .notRequired(),
  produtoPredominante: yup
    .object()
    .shape({
      tipoCarga: yup
        .number()
        .required("Tipo de carga da ANTT deve ser válido.")
        .min(1, "Tipo de carga da ANTT deve ser válido.")
        .max(11, "Tipo de carga da ANTT deve ser válido."),
      nomeProduto: yup
        .string()
        .required("Nome do produto predominante é obrigatório.")
        .test(
          "nome-produto-len",
          "Nome do produto predominante deve ter entre 2 e 120 caracteres conforme manual SEFAZ.",
          (v) => Boolean(v && v.trim().length >= 2 && v.trim().length <= 120)
        ),
      codigoEAN: yup
        .string()
        .nullable()
        .notRequired()
        .test(
          "codigo-ean-valido",
          "O código de barras deve ser GTIN-8, GTIN-12, GTIN-13, GTIN-14 ou 'SEM GTIN'.",
          (v) => {
            if (!v || v.trim() === "") return true;
            const trimmed = v.trim().toUpperCase();
            if (trimmed === "SEM GTIN") return true;
            const limpo = v.replace(/\D/g, "");
            return [8, 12, 13, 14].includes(limpo.length);
          }
        ),
      ncm: yup
        .string()
        .nullable()
        .notRequired()
        .test(
          "rejeicao-301-ncm",
          "O NCM do produto predominante da carga lotação deve ser informado (Rejeição 301).",
          function (v) {
            const cepCarreg = this.parent?.cepCarregamento;
            const cepDesc = this.parent?.cepDescarregamento;
            const temCep = Boolean(
              (cepCarreg && cepCarreg.trim() !== "") ||
              (cepDesc && cepDesc.trim() !== "")
            );
            if (temCep) {
              return Boolean(v && v.trim() !== "");
            }
            return true;
          }
        )
        .test(
          "ncm-valido",
          "NCM deve conter exatamente 8 dígitos numéricos.",
          (v) => {
            if (!v || v.trim() === "") return true;
            const limpo = v.replace(/\D/g, "");
            return limpo.length === 8;
          }
        ),
      cepCarregamento: yup
        .string()
        .nullable()
        .notRequired()
        .test(
          "cep-carregamento-codependente",
          "Quando informado o CEP de descarregamento, o CEP de carregamento também deve ser informado.",
          function (v) {
            const cepDesc = this.parent?.cepDescarregamento;
            if (cepDesc && cepDesc.trim() !== "") {
              return Boolean(v && v.trim() !== "");
            }
            return true;
          }
        )
        .test(
          "cep-carregamento-valido",
          "CEP de carregamento deve conter 8 dígitos numéricos.",
          (v) => {
            if (!v || v.trim() === "") return true;
            const limpo = v.replace(/\D/g, "");
            return limpo.length === 8;
          }
        ),
      cepDescarregamento: yup
        .string()
        .nullable()
        .notRequired()
        .test(
          "cep-descarregamento-codependente",
          "Quando informado o CEP de carregamento, o CEP de descarregamento também deve ser informado.",
          function (v) {
            const cepCarreg = this.parent?.cepCarregamento;
            if (cepCarreg && cepCarreg.trim() !== "") {
              return Boolean(v && v.trim() !== "");
            }
            return true;
          }
        )
        .test(
          "cep-descarregamento-valido",
          "CEP de descarregamento deve conter 8 dígitos numéricos.",
          (v) => {
            if (!v || v.trim() === "") return true;
            const limpo = v.replace(/\D/g, "");
            return limpo.length === 8;
          }
        ),
    })
    .default(undefined)
    .nullable()
    .notRequired(),
});

export type TotaisFormType = yup.InferType<typeof TotaisSchema>;

export const ContratantesSchema = yup
  .object()
  .shape({
    contratantes: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            nomeContratante: yup
              .string()
              .nullable()
              .notRequired()
              .max(60, "Nome do contratante deve conter no máximo 60 caracteres."),
            documento: yup
              .string()
              .nullable()
              .notRequired()
              .transform((v) => (v ? v.replace(/[.\-/\s]/g, "").toUpperCase() : ""))
              .test(
                "doc-contratante-valido",
                "Documento (CPF ou CNPJ) do contratante é inválido.",
                (v) => !v || validateCpfOuCnpj(v)
              ),
            cpf: yup
              .string()
              .nullable()
              .notRequired()
              .test(
                "cpf-contratante-valido",
                "CPF do contratante é inválido.",
                (v) => {
                  if (!v || v.trim() === "") return true;
                  return validateCpf(v);
                }
              ),
            cnpj: yup
              .string()
              .nullable()
              .notRequired()
              .test(
                "cnpj-contratante-valido",
                "CNPJ do contratante é inválido.",
                (v) => {
                  if (!v || v.trim() === "") return true;
                  return validateCnpj(v);
                }
              ),
            idEstrangeiro: yup
              .string()
              .nullable()
              .notRequired()
              .test(
                "id-estrangeiro-contratante-len",
                "Identificador estrangeiro do contratante deve ter entre 2 e 20 caracteres.",
                (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 20)
              ),
          })
          .test(
            "contratante-doc-obrigatorio",
            "Identificação do contratante é obrigatória (informe CPF, CNPJ ou Identificação Estrangeira).",
            function (c) {
              if (!c) return true;
              return Boolean(
                (c.documento && c.documento.trim() !== "") ||
                (c.cpf && c.cpf.trim() !== "") ||
                (c.cnpj && c.cnpj.trim() !== "") ||
                (c.idEstrangeiro && c.idEstrangeiro.trim() !== "")
              );
            }
          )
          .test(
            "contratante-apenas-um-doc",
            "Informe apenas um documento para o contratante (CPF/CNPJ ou Identificação Estrangeira).",
            function (c) {
              if (!c) return true;
              const hasDoc = Boolean(
                (c.documento && c.documento.trim() !== "") ||
                (c.cpf && c.cpf.trim() !== "") ||
                (c.cnpj && c.cnpj.trim() !== "")
              );
              const hasIdEst = Boolean(c.idEstrangeiro && c.idEstrangeiro.trim() !== "");
              return !(hasDoc && hasIdEst);
            }
          )
      )
      .notRequired(),
    ciots: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            nomeResponsavel: yup.string().nullable().notRequired(),
            ciot: yup
              .string()
              .required("Código CIOT é obrigatório.")
              .test(
                "ciot-12",
                "CIOT deve conter exatamente 12 dígitos numéricos conforme ANTT.",
                (v) => Boolean(v && v.replace(/\D/g, "").length === 12)
              )
              .test(
                "ciot-apenas-numeros",
                "CIOT deve conter apenas números.",
                (v) => !v || /^\d+$/.test(v.replace(/\D/g, ""))
              ),
            documento: yup
              .string()
              .transform((v) => (v ? v.replace(/[.\-/\s]/g, "").toUpperCase() : ""))
              .test(
                "doc-ciot-obrigatorio",
                "CPF ou CNPJ do responsável pelo CIOT é obrigatório.",
                function (v) {
                  const parent = this.parent;
                  return Boolean(
                    (v && v.trim() !== "") ||
                    (parent?.cpf && String(parent.cpf).trim() !== "") ||
                    (parent?.cnpj && String(parent.cnpj).trim() !== "")
                  );
                }
              )
              .test(
                "doc-ciot-valido",
                "Documento (CPF ou CNPJ) do responsável pelo CIOT é inválido.",
                (v) => !v || validateCpfOuCnpj(v)
              ),
            cpf: yup
              .string()
              .nullable()
              .notRequired()
              .test(
                "cpf-ciot-valido",
                "CPF do responsável pelo CIOT é inválido.",
                (v) => {
                  if (!v || v.trim() === "") return true;
                  return validateCpf(v);
                }
              ),
            cnpj: yup
              .string()
              .nullable()
              .notRequired()
              .test(
                "cnpj-ciot-valido",
                "CNPJ do responsável pelo CIOT é inválido.",
                (v) => {
                  if (!v || v.trim() === "") return true;
                  return validateCnpj(v);
                }
              ),
          })
          .test(
            "ciot-doc-obrigatorio",
            "CPF ou CNPJ do responsável pela geração do CIOT é obrigatório.",
            function (c) {
              if (!c) return true;
              return Boolean(
                (c.documento && c.documento.trim() !== "") ||
                (c.cpf && c.cpf.trim() !== "") ||
                (c.cnpj && c.cnpj.trim() !== "")
              );
            }
          )
      )
      .notRequired(),
    valesPedagio: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            nomeFornecedoraValePedagio: yup.string().nullable().notRequired(),
            cnpjEmitenteValePedagio: yup
              .string()
              .required("CNPJ da empresa fornecedora do Vale-Pedágio é obrigatório.")
              .transform((v) =>
                v ? v.replace(/[^A-Za-z0-9]/g, "").toUpperCase() : ""
              )
              .test(
                "cnpj-fornecedor-vale-valido",
                "CNPJ da fornecedora do Vale-Pedágio é inválido.",
                (v) => Boolean(v && validateCnpj(v))
              ),
            documentoResponsavelPagamento: yup
              .string()
              .nullable()
              .notRequired()
              .transform((v) =>
                v ? v.replace(/[^A-Za-z0-9]/g, "").toUpperCase() : ""
              )
              .test(
                "doc-resp-vale-valido",
                "Documento (CPF ou CNPJ) do responsável pelo pagamento do Vale-Pedágio é inválido.",
                (v) => !v || validateCpfOuCnpj(v)
              ),
            nomeResponsavelPagamento: yup.string().nullable().notRequired(),
            cnpjResponsavelPagamento: yup
              .string()
              .nullable()
              .notRequired()
              .test(
                "cnpj-resp-vale-valido",
                "CNPJ do responsável pelo pagamento do Vale-Pedágio é inválido.",
                (v) => {
                  if (!v || v.trim() === "") return true;
                  return validateCnpj(v);
                }
              ),
            cpfResponsavelPagamento: yup
              .string()
              .nullable()
              .notRequired()
              .test(
                "cpf-resp-vale-valido",
                "CPF do responsável pelo pagamento do Vale-Pedágio é inválido.",
                (v) => {
                  if (!v || v.trim() === "") return true;
                  return validateCpf(v);
                }
              ),
            numeroComprovante: yup
              .string()
              .required("Número do comprovante de compra do Vale-Pedágio é obrigatório.")
              .max(20, "Número do comprovante deve conter no máximo 20 caracteres."),
            valorValePedagio: yup
              .number()
              .required("Valor do Vale-Pedágio é obrigatório e deve ser maior que zero.")
              .typeError("Valor do Vale-Pedágio deve ser numérico")
              .moreThan(0, "Valor do Vale-Pedágio deve ser maior que zero.")
              .max(999999999999.99, "Valor do Vale-Pedágio inválido."),
            tipoValePedagio: yup
              .string()
              .nullable()
              .notRequired()
              .test(
                "tipo-vale-pedagio",
                "Tipo de Vale-Pedágio deve ser 01 (Tag eletrônica), 02 (Cupom/Voucher) ou 03 (Cartão magnético).",
                (v) => !v || ["01", "02", "03"].includes(v)
              ),
            categoriaCombinacaoVeicular: yup
              .string()
              .required(
                "A categoria de combinação veicular deve ser preenchida para o grupo vale pedágio (Rejeição 731)."
              )
              .matches(
                /^(02|04|06|07|08|10|11|12|13|14)$/,
                "Categoria de combinação veicular inválida (Rejeição 731). Valores aceitos conforme ANTT: 02, 04, 06, 07, 08, 10, 11, 12, 13 ou 14."
              ),
          })
      )
      .notRequired(),
  })
  .test(
    "rejeicao-578",
    "Informações dos tomadores é obrigatória para esta operação (Rejeição 578). Informe ao menos um Contratante (infContratante), CIOT (infCIOT) ou Vale-Pedágio (valePed).",
    function (input: any) {
      if (!input) return true;
      const mdfe = getLocal<MDFeInput>(STORAGEMDFE_KEY);
      if (!mdfe || mdfe.tipoEmitente === undefined || mdfe.tipoEmitente === null) return true;
      const isRodoviario = Number(mdfe.modal ?? 1) === 1;
      const tipoEmit = Number(mdfe.tipoEmitente);
      const tipoTransp = mdfe.tipoTransportador;
      const exigeTomador =
        isRodoviario &&
        (tipoEmit === 1 ||
          tipoEmit === 3 ||
          (tipoEmit === 2 && Boolean(tipoTransp)));

      if (!exigeTomador) return true;

      const hasContratante = Boolean(input.contratantes && input.contratantes.length > 0);
      const hasCiot = Boolean(input.ciots && input.ciots.length > 0);
      const hasValePedagio = Boolean(input.valesPedagio && input.valesPedagio.length > 0);

      if (!hasContratante && !hasCiot && !hasValePedagio) {
        return this.createError({
          path: "contratantes",
          message:
            "Informações dos tomadores é obrigatória para esta operação (Rejeição 578). Informe ao menos um Contratante (infContratante), CIOT (infCIOT) ou Vale-Pedágio (valePed).",
        });
      }
      return true;
    }
  );

export type ContratantesFormType = yup.InferType<typeof ContratantesSchema>;

export const distribuirValorParcelas = (
  valorTotal: number,
  quantidade: number
): number[] => {
  if (!(quantidade > 0)) return [];
  const totalCentavos = Math.round((Number(valorTotal) || 0) * 100);
  const base = Math.floor(totalCentavos / quantidade);
  const resto = totalCentavos - base * quantidade;
  return Array.from(
    { length: quantidade },
    (_, i) => (base + (i === quantidade - 1 ? resto : 0)) / 100
  );
};

export const PagamentoSchema = yup.object().shape({
  pagamentos: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          nomeBeneficiario: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "nome-beneficiario-len",
              "Nome do responsável pelo pagamento deve ter entre 2 e 60 caracteres.",
              (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 60)
            ),
          documento: yup
            .string()
            .nullable()
            .notRequired()
            .transform((v) => (v ? v.replace(/[.\-/\s]/g, "").toUpperCase() : ""))
            .test(
              "doc-pagamento-valido",
              "Documento (CPF ou CNPJ) do beneficiário/responsável é inválido.",
              (v) => !v || validateCpfOuCnpj(v)
            ),
          cpfBeneficiario: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cpf-beneficiario-valido",
              "CPF do responsável pelo pagamento é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCpf(v);
              }
            ),
          cnpjBeneficiario: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cnpj-beneficiario-valido",
              "CNPJ do responsável pelo pagamento é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCnpj(v);
              }
            ),
          idEstrangeiroBeneficiario: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "id-estrangeiro-beneficiario-len",
              "Identificador estrangeiro do responsável pelo pagamento deve ter entre 2 e 20 caracteres.",
              (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 20)
            ),
          valorTotalContrato: yup
            .number()
            .required("O valor total do contrato deve ser maior que zero.")
            .typeError("O valor total do contrato deve ser maior que zero.")
            .moreThan(0, "O valor total do contrato deve ser maior que zero."),
          formaPagamento: yup
            .number()
            .required("Forma de pagamento do frete deve ser válida (0 = À Vista, 1 = A Prazo).")
            .oneOf([0, 1], "Forma de pagamento do frete deve ser válida (0 = À Vista, 1 = A Prazo)."),
          documentoResponsavelPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .transform((v) => (v ? v.replace(/[.\-/\s]/g, "").toUpperCase() : ""))
            .test(
              "doc-resp-pagamento-valido",
              "Documento (CPF ou CNPJ) do responsável pelo pagamento é inválido.",
              (v) => !v || validateCpfOuCnpj(v)
            ),
          cnpjResponsavelPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cnpj-resp-pagamento-valido",
              "CNPJ do responsável pelo pagamento é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCnpj(v);
              }
            ),
          cpfResponsavelPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cpf-resp-pagamento-valido",
              "CPF do responsável pelo pagamento é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCpf(v);
              }
            ),
          idEstrangeiroResponsavelPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "id-estrangeiro-resp-len",
              "Identificador estrangeiro do responsável pelo pagamento deve ter entre 2 e 20 caracteres.",
              (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 20)
            ),
          cnpjInstituicaoPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cnpj-ipef-valido",
              "CNPJ da Instituição de Pagamento Eletrônico do Frete (CNPJIPEF) é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCnpj(v);
              }
            ),
          chavePixBancario: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "pix-comprimento",
              "Chave PIX deve ter entre 2 e 60 caracteres.",
              (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 60)
            ),
          valorAdiantamento: yup
            .number()
            .nullable()
            .notRequired()
            .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
            .min(0, "Valor do adiantamento deve ser maior ou igual a zero.")
            .test(
              "rejeicao-739-adiantamento-a-vista",
              "Valor do adiantamento não pode ser informado para pagamento a vista (Rejeição 739).",
              function (v) {
                if (!v || v === 0) return true;
                const formaPag = Number(this.parent?.formaPagamento);
                if (formaPag === 0) {
                  return false;
                }
                return true;
              }
            )
            .test(
              "rejeicao-740-adiantamento-menor-contrato",
              "Valor do adiantamento deve ser menor que o valor total do contrato (Rejeição 740).",
              function (v) {
                if (!v || v === 0) return true;
                const formaPag = Number(this.parent?.formaPagamento);
                const valorTotal = Number(this.parent?.valorTotalContrato) || 0;
                if (formaPag === 1 && v >= valorTotal) {
                  return false;
                }
                return true;
              }
            ),
          tipoAntecipacao: yup
            .number()
            .nullable()
            .notRequired()
            .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
            .test(
              "tipo-antecipacao-valido",
              "Tipo de antecipação deve ser válido (0 = Não permite, 1 = Permite, 2 = Permite mediante confirmação).",
              (v) => v === null || v === undefined || (v >= 0 && v <= 2)
            ),
          codigoBanco: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "codigo-banco-req-se-agencia",
              "Código do banco é obrigatório ao informar dados bancários da conta.",
              function (v) {
                const ag = this.parent?.codigoAgencia;
                if (ag && ag.trim() !== "") {
                  return Boolean(v && v.trim() !== "");
                }
                return true;
              }
            )
            .test(
              "codigo-banco-tamanho",
              "Código do banco deve ter entre 3 e 5 dígitos.",
              (v) => {
                if (!v || v.trim() === "") return true;
                const limpo = v.replace(/\D/g, "");
                return limpo.length >= 3 && limpo.length <= 5;
              }
            )
            .test(
              "codigo-banco-numeros",
              "Código do banco deve conter apenas dígitos numéricos.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return /^\d+$/.test(v.trim());
              }
            ),
          codigoAgencia: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "codigo-agencia-req-se-banco",
              "Código da agência é obrigatório ao informar dados bancários da conta.",
              function (v) {
                const b = this.parent?.codigoBanco;
                if (b && b.trim() !== "") {
                  return Boolean(v && v.trim() !== "");
                }
                return true;
              }
            )
            .test(
              "codigo-agencia-tamanho",
              "Código da agência deve ter entre 1 e 10 dígitos.",
              (v) => {
                if (!v || v.trim() === "") return true;
                const limpo = v.replace(/\D/g, "");
                return limpo.length >= 1 && limpo.length <= 10;
              }
            ),
          indicadorPagamentoBancario: yup
            .number()
            .nullable()
            .notRequired()
            .transform((val, orig) => (orig === "" || isNaN(val) ? null : val)),
          componentes: yup
            .array()
            .of(
              yup.object().shape({
                tipoComponente: yup
                  .number()
                  .required("Tipo do componente de pagamento do frete deve ser válido.")
                  .oneOf([1, 2, 3, 4, 99], "Tipo do componente de pagamento do frete deve ser válido."),
                valorComponente: yup
                  .number()
                  .required("Valor do componente deve ser maior que zero.")
                  .typeError("Valor do componente deve ser maior que zero.")
                  .moreThan(0, "Valor do componente deve ser maior que zero."),
                descricaoOutro: yup.string().when("tipoComponente", {
                  is: (val: number) => Number(val) === 99,
                  then: (schema) =>
                    schema
                      .required("Descrição do componente (xComp) é obrigatória quando o tipo for 'Outros'.")
                      .min(2, "Descrição do componente deve ter entre 2 e 60 caracteres.")
                      .max(60, "Descrição do componente deve ter entre 2 e 60 caracteres."),
                  otherwise: (schema) =>
                    schema
                      .nullable()
                      .notRequired()
                      .test(
                        "desc-outro-len",
                        "Descrição do componente deve ter entre 2 e 60 caracteres.",
                        (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 60)
                      ),
                }),
              })
            )
            .required("Deve haver pelo menos um componente da tarifa do frete.")
            .min(1, "Deve haver pelo menos um componente da tarifa do frete."),
          parcelas: yup
            .array()
            .of(
              yup.object().shape({
                numeroParcela: yup
                  .number()
                  .required("Número da parcela deve estar entre 1 e 999.")
                  .typeError("Número da parcela deve estar entre 1 e 999.")
                  .min(1, "Número da parcela deve estar entre 1 e 999.")
                  .max(999, "Número da parcela deve estar entre 1 e 999."),
                dataVencimento: yup
                  .string()
                  .required("Data de vencimento da parcela é obrigatória e deve ser válida.")
                  .test(
                    "data-venc-valida",
                    "Data de vencimento da parcela é obrigatória e deve ser válida.",
                    (v) => Boolean(v && moment(v, ["YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601]).isValid())
                  ),
                valorParcela: yup
                  .number()
                  .required("Valor da parcela deve ser maior que zero.")
                  .typeError("Valor da parcela deve ser maior que zero.")
                  .moreThan(0, "Valor da parcela deve ser maior que zero."),
              })
            )
            .when("formaPagamento", {
              is: (val: number) => Number(val) === 1,
              then: (schema) =>
                schema
                  .required("Informações de parcelas (infPrazo) são obrigatórias para pagamento a prazo.")
                  .min(1, "Informações de parcelas (infPrazo) são obrigatórias para pagamento a prazo."),
              otherwise: (schema) => schema.notRequired(),
            }),
        })
        .test(
          "pagamento-id-responsavel",
          "Identificação do responsável pelo pagamento é obrigatória (informe CPF, CNPJ ou Identificação Estrangeira).",
          function (p) {
            if (!p) return true;
            const hasId = Boolean(
              (p.documento && p.documento.trim() !== "") ||
              (p.cpfBeneficiario && p.cpfBeneficiario.trim() !== "") ||
              (p.cnpjBeneficiario && p.cnpjBeneficiario.trim() !== "") ||
              (p.idEstrangeiroBeneficiario && p.idEstrangeiroBeneficiario.trim() !== "") ||
              (p.cpfResponsavelPagamento && p.cpfResponsavelPagamento.trim() !== "") ||
              (p.cnpjResponsavelPagamento && p.cnpjResponsavelPagamento.trim() !== "") ||
              (p.idEstrangeiroResponsavelPagamento && p.idEstrangeiroResponsavelPagamento.trim() !== "")
            );
            if (!hasId) {
              return this.createError({
                path: `${this.path}.documento`,
                message: "Identificação do responsável pelo pagamento é obrigatória (informe CPF, CNPJ ou Identificação Estrangeira).",
              });
            }
            return true;
          }
        )
        .test(
          "pagamento-infbanc-obrigatorio",
          "Informações bancárias de recebimento do frete (infBanc) são obrigatórias. Informe uma das opções: dados bancários (banco e agência), CNPJ da IPEF ou Chave PIX.",
          function (p) {
            if (!p) return true;
            const temBanc = Boolean(
              (p.codigoBanco && p.codigoBanco.trim() !== "") ||
              (p.codigoAgencia && p.codigoAgencia.trim() !== "")
            );
            const temIpef = Boolean(p.cnpjInstituicaoPagamento && p.cnpjInstituicaoPagamento.trim() !== "");
            const temPix = Boolean(p.chavePixBancario && p.chavePixBancario.trim() !== "");
            if (!temBanc && !temIpef && !temPix) {
              return this.createError({
                path: `${this.path}.chavePixBancario`,
                message: "Informações bancárias de recebimento do frete (infBanc) são obrigatórias. Informe uma das opções: dados bancários (banco e agência), CNPJ da IPEF ou Chave PIX.",
              });
            }
            return true;
          }
        )
        .test(
          "pagamento-infbanc-mutex",
          "Dados bancários (banco/agência), CNPJ da IPEF e Chave PIX são mutuamente exclusivos no grupo infBanc — informe apenas uma das modalidades.",
          function (p) {
            if (!p) return true;
            const temBanc = Boolean(
              (p.codigoBanco && p.codigoBanco.trim() !== "") ||
              (p.codigoAgencia && p.codigoAgencia.trim() !== "")
            );
            const temIpef = Boolean(p.cnpjInstituicaoPagamento && p.cnpjInstituicaoPagamento.trim() !== "");
            const temPix = Boolean(p.chavePixBancario && p.chavePixBancario.trim() !== "");
            let count = 0;
            if (temBanc) count++;
            if (temIpef) count++;
            if (temPix) count++;
            return count <= 1;
          }
        )
        .test(
          "rejeicao-746",
          "A soma dos componentes do pagamento deve ser igual ao valor do contrato (Rejeição 746).",
          function (p) {
            if (!p) return true;
            const valorTotalContrato = Number(p.valorTotalContrato) || 0;
            const componentes = p.componentes || [];
            if (componentes.length === 0) return true;
            const somaComponentes = componentes.reduce(
              (acc: number, c: any) => acc + (Number(c?.valorComponente) || 0),
              0
            );
            if (Math.abs(somaComponentes - valorTotalContrato) > 0.01) {
              const somaFmt = somaComponentes.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              const contratoFmt = valorTotalContrato.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              return this.createError({
                path: `${this.path}.valorTotalContrato`,
                message: `A soma dos componentes do pagamento (${somaFmt}) deve ser igual ao valor do contrato (${contratoFmt}) (Rejeição 746).`,
              });
            }
            return true;
          }
        )
    )
    .notRequired(),
});

export type PagamentoFormType = yup.InferType<typeof PagamentoSchema>;

export const FinalizacaoSchema = yup.object().shape({
  emissaoDataHora: yup.string().required("Data de emissão é obrigatória"),
  serie: yup
    .number()
    .nullable()
    .notRequired()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null || isNaN(value)
        ? null
        : value
    )
    .min(0, "Série deve ter no máximo 3 dígitos.")
    .max(999, "Série deve ter no máximo 3 dígitos."),
  numeroMDFe: yup
    .number()
    .nullable()
    .notRequired()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null || isNaN(value)
        ? null
        : value
    )
    .min(0, "O número do MDF-e deve ser maior ou igual a zero.")
    .max(999999999, "Número do MDF-e deve ter no máximo 9 dígitos."),
  informacoesAdicionaisFisco: yup
    .string()
    .nullable()
    .notRequired()
    .max(2000, "Informações adicionais para o Fisco devem conter no máximo 2000 caracteres."),
  informacoesComplementaresContribuinte: yup
    .string()
    .nullable()
    .notRequired()
    .max(5000, "Informações complementares devem conter no máximo 5000 caracteres."),
});

export type FinalizacaoFormType = yup.InferType<typeof FinalizacaoSchema>;

/**
 * Schema consolidado de validação para a inclusão completa do MDF-e.
 * Espelha integralmente as regras do MDFeInputValidator e validadores filhos
 * do backend (Fiscal.Domain.Validation.MDFe).
 */
export const MDFeInputSchema = yup.object().shape({
  serie: yup
    .number()
    .required("Série deve ter no máximo 3 dígitos.")
    .typeError("Série deve ser numérica.")
    .min(0, "Série deve ter no máximo 3 dígitos.")
    .max(999, "Série deve ter no máximo 3 dígitos."),

  numeroMDFe: yup
    .number()
    .required("Número do MDF-e deve ter no máximo 9 dígitos.")
    .typeError("Número do MDF-e deve ser numérico.")
    .min(0, "Número do MDF-e deve ter no máximo 9 dígitos.")
    .max(999999999, "Número do MDF-e deve ter no máximo 9 dígitos."),

  tipoEmitente: yup
    .number()
    .required("Tipo de emitente deve ser válido.")
    .oneOf([1, 2, 3], "Tipo de emitente deve ser válido."),

  tipoTransportador: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
    .oneOf([1, 2, 3], "Tipo de transportador deve ser válido.")
    .test(
      "rejeicao-745",
      "O tipo de transportador não pode ser informado quando não estiver informado proprietário do veículo de tração.",
      function (tipoTransp) {
        if (!tipoTransp) return true;
        const prop = this.parent?.proprietarioVeiculoTracao;
        return Boolean(
          prop && (prop.documento || prop.cpf || prop.cnpj || prop.nome)
        );
      }
    )
    .test(
      "rejeicao-744",
      "Para proprietário pessoa jurídica (CNPJ), o tipo de transportador deve ser ETC ou CTC.",
      function (tipoTransp) {
        if (!tipoTransp) return true;
        const prop = this.parent?.proprietarioVeiculoTracao;
        const docLimpo = prop?.documento
          ? prop.documento.replace(/[.\-/\s]/g, "")
          : (prop?.cnpj || "");
        if (docLimpo && docLimpo.trim().length === 14) {
          return Number(tipoTransp) === 1 || Number(tipoTransp) === 3;
        }
        return true;
      }
    ),

  modal: yup
    .number()
    .required("Modal deve ser válido.")
    .oneOf([1, 2, 3, 4], "Modal deve ser válido."),

  ufInicio: yup
    .string()
    .required("UF de início é obrigatória.")
    .test("uf-inicio-len", "UF de início deve conter 2 letras.", (v) => !v || v.trim().length === 2),

  ufFim: yup
    .string()
    .required("UF final é obrigatória.")
    .test("uf-fim-len", "UF final deve conter 2 letras.", (v) => !v || v.trim().length === 2),

  rntrc: yup
    .string()
    .nullable()
    .notRequired()
    .test(
      "rntrc-tamanho",
      "RNTRC deve conter exatamente 8 dígitos numéricos.",
      (v) => {
        if (!v || v.trim() === "") return true;
        const limpo = v.replace(/\D/g, "");
        return limpo.length === 8;
      }
    )
    .test(
      "rntrc-digitos",
      "RNTRC deve conter apenas dígitos numéricos.",
      (v) => {
        if (!v || v.trim() === "") return true;
        return /^\d+$/.test(v.trim());
      }
    ),

  // Veículo de Tração
  placaVeiculoTracao: yup
    .string()
    .required("Placa do veículo de tração é obrigatória.")
    .test(
      "placa-tracao-len",
      "Placa deve conter 7 caracteres.",
      (v) => Boolean(v && v.replace(/[^A-Za-z0-9]/g, "").length === 7)
    ),

  taraVeiculoTracao: yup
    .number()
    .required("Tara do veículo de tração deve ter entre 1 e 6 dígitos.")
    .typeError("Tara do veículo de tração deve ter entre 1 e 6 dígitos.")
    .min(1, "Tara do veículo de tração deve ter entre 1 e 6 dígitos.")
    .max(999999, "Tara do veículo de tração deve ter entre 1 e 6 dígitos."),

  capacidadeKGVeiculoTracao: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
    .test(
      "cap-kg-tracao",
      "Capacidade em KG do veículo de tração deve ter no máximo 6 dígitos (0 a 999999).",
      (v) => v === null || v === undefined || (v >= 0 && v <= 999999)
    ),

  capacidadeM3VeiculoTracao: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
    .test(
      "cap-m3-tracao",
      "Capacidade em M³ do veículo de tração deve ter no máximo 3 dígitos (0 a 999).",
      (v) => v === null || v === undefined || (v >= 0 && v <= 999)
    ),

  ufVeiculoTracao: yup
    .string()
    .required("UF do veículo de tração é obrigatória.")
    .test(
      "uf-tracao-len",
      "UF do veículo deve conter 2 caracteres.",
      (v) => !v || v.trim().length === 2
    ),

  proprietarioVeiculoTracao: ProprietarioVeiculoSchema
    .nullable()
    .notRequired()
    .default(undefined)
    .transform((val) => {
      if (!val) return undefined;
      const hasAnyField = Boolean(
        val.documento || val.cpf || val.cnpj || val.nome || val.rntrc || val.ie
      );
      return hasAnyField ? val : undefined;
    }),

  tipoRodadoVeiculoTracao: yup
    .number()
    .required("Tipo de rodado deve ser válido.")
    .oneOf([1, 2, 3, 4, 5, 6], "Tipo de rodado deve ser válido."),

  tipoCarroceriaVeiculoTracao: yup
    .number()
    .required("Tipo de carroceria deve ser válido.")
    .oneOf([0, 1, 2, 3, 4, 5], "Tipo de carroceria deve ser válido."),

  reboques: yup
    .array()
    .of(
      yup.object().shape({
        placa: yup
          .string()
          .required("Placa do reboque é obrigatória.")
          .test(
            "placa-reboque-len",
            "Placa do reboque deve conter 7 caracteres.",
            (v) => Boolean(v && v.replace(/[^A-Za-z0-9]/g, "").length === 7)
          ),
        tara: yup
          .number()
          .required("Tara do reboque deve ter entre 1 e 6 dígitos.")
          .typeError("Tara do reboque deve ter entre 1 e 6 dígitos.")
          .min(1, "Tara do reboque deve ter entre 1 e 6 dígitos.")
          .max(999999, "Tara do reboque deve ter entre 1 e 6 dígitos."),
        capacidadeKG: yup
          .number()
          .required("Capacidade em KG do reboque é obrigatória.")
          .typeError("Capacidade em KG do reboque é obrigatória.")
          .min(1, "Capacidade em KG do reboque deve ter entre 1 e 6 dígitos.")
          .max(999999, "Capacidade em KG do reboque deve ter entre 1 e 6 dígitos."),
        capacidadeM3: yup
          .number()
          .nullable()
          .notRequired()
          .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
          .test(
            "cap-m3-reboque",
            "Capacidade em M³ do reboque deve ter no máximo 3 dígitos (0 a 999).",
            (v) => v === null || v === undefined || (v >= 0 && v <= 999)
          ),
        uf: yup
          .string()
          .required("UF do reboque é obrigatória.")
          .test(
            "uf-reboque-len",
            "UF do reboque deve conter 2 caracteres.",
            (v) => !v || v.trim().length === 2
          ),
        tipoCarroceria: yup
          .number()
          .required("Tipo de carroceria do reboque deve ser válido.")
          .oneOf([0, 1, 2, 3, 4, 5], "Tipo de carroceria do reboque deve ser válido."),
      })
    )
    .notRequired(),

  // Totais da Carga
  valorTotalCarga: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
    .test(
      "valor-total-carga-ge0",
      "Valor total da carga deve ser maior ou igual a zero.",
      (v) => v === null || v === undefined || v >= 0
    ),

  pesoBrutoTotal: yup
    .number()
    .nullable()
    .notRequired()
    .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
    .test(
      "peso-bruto-total-ge0",
      "Peso bruto total deve ser maior ou igual a zero.",
      (v) => v === null || v === undefined || v >= 0
    ),

  unidadeMedidaPeso: yup
    .number()
    .required("Unidade de medida do peso deve ser válida.")
    .oneOf([1, 2], "Unidade de medida do peso deve ser válida."),

  // Listas obrigatórias
  municipiosCarregamento: yup
    .array()
    .of(
      yup.object().shape({
        codigoMunicipioCarregamento: yup
          .number()
          .required("Código do município de carregamento deve ser um código IBGE válido de 7 dígitos.")
          .typeError("Código do município de carregamento deve ser um código IBGE válido de 7 dígitos.")
          .min(1000000, "Código do município de carregamento deve ser um código IBGE válido de 7 dígitos.")
          .max(9999999, "Código do município de carregamento deve ser um código IBGE válido de 7 dígitos.")
          .test(
            "rejeicao-456-uf-carregamento",
            function (val) {
              if (!val) return true;
              const ufInicio =
                this.from?.find((f: any) => f?.value?.ufInicio !== undefined)?.value?.ufInicio ||
                getLocal<MDFeInput>(STORAGEMDFE_KEY)?.ufInicio;
              if (!ufInicio) return true;
              const valido = validarMunicipioPertenceUf(val, ufInicio);
              if (!valido) {
                return this.createError({
                  message: `Código de Município (${val}) diverge da UF de carregamento do MDF-e (${ufInicio}) (Rejeição 456).`,
                });
              }
              return true;
            }
          ),
        nomeMunicipioCarregamento: yup
          .string()
          .required("Nome do município de carregamento é obrigatório.")
          .min(2, "Nome do município de carregamento deve ter entre 2 e 60 caracteres.")
          .max(60, "Nome do município de carregamento deve ter entre 2 e 60 caracteres."),
      })
    )
    .required("Deve haver pelo menos um município de carregamento.")
    .min(1, "Deve haver pelo menos um município de carregamento.")
    .test(
      "rejeicao-456-municipios-carregamento",
      function (municipios) {
        const ufInicio = this.parent?.ufInicio;
        if (!ufInicio || !municipios || !Array.isArray(municipios)) return true;
        for (let i = 0; i < municipios.length; i++) {
          const cod = municipios[i]?.codigoMunicipioCarregamento;
          if (cod && !validarMunicipioPertenceUf(cod, ufInicio)) {
            return this.createError({
              path: `${this.path}[${i}].codigoMunicipioCarregamento`,
              message: `Código de Município (${cod}) diverge da UF de carregamento do MDF-e (${ufInicio}) (Rejeição 456).`,
            });
          }
        }
        return true;
      }
    ),

  municipiosDescarga: yup
    .array()
    .of(
      yup.object().shape({
        codigoMunicipioDescarga: yup
          .number()
          .required("Código do município de descarregamento deve ser um código IBGE válido de 7 dígitos.")
          .typeError("Código do município de descarregamento deve ser um código IBGE válido de 7 dígitos.")
          .min(1000000, "Código do município de descarregamento deve ser um código IBGE válido de 7 dígitos.")
          .max(9999999, "Código do município de descarregamento deve ser um código IBGE válido de 7 dígitos.")
          .test(
            "rejeicao-612-uf-descarga",
            function (val) {
              if (!val) return true;
              const ufFim =
                this.from?.find((f: any) => f?.value?.ufFim !== undefined)?.value?.ufFim ||
                getLocal<MDFeInput>(STORAGEMDFE_KEY)?.ufFim;
              if (!ufFim) return true;
              const valido = validarMunicipioPertenceUf(val, ufFim);
              if (!valido) {
                return this.createError({
                  message: `Código de Município (${val}) diverge da UF de descarga do MDF-e (${ufFim}) (Rejeição 612).`,
                });
              }
              return true;
            }
          ),
        nomeMunicipioDescarga: yup
          .string()
          .required("Nome do município de descarregamento é obrigatório.")
          .min(2, "Nome do município de descarregamento deve ter entre 2 e 60 caracteres.")
          .max(60, "Nome do município de descarregamento deve ter entre 2 e 60 caracteres."),
        documentos: yup
          .array()
          .of(
            yup.object().shape({
              chaveAcesso: yup
                .string()
                .required("Chave de acesso é obrigatória.")
                .transform((v) => (v ? v.replace(/[\s.-]/g, "").toUpperCase() : ""))
                .test(
                  "chave-44-digitos",
                  "Chave de acesso deve conter exatamente 44 dígitos.",
                  (val) => Boolean(val && val.length === 44)
                )
                .test(
                  "chave-estrutura",
                  "Chave de acesso deve conter apenas dígitos numéricos.",
                  (val) => !val || validateEstruturaChaveAcesso(val)
                )
                .test(
                  "chave-modelo",
                  "Modelo da chave de acesso incompatível (deve ser 57 para CT-e ou 55 para NF-e).",
                  function (val) {
                    if (!val || val.length !== 44) return true;
                    const tipoDocumento = this.parent?.tipoDocumento;
                    return validateModeloChaveAcesso(val, Number(tipoDocumento));
                  }
                )
                .test(
                  "chave-dv",
                  "Dígito verificador da chave de acesso é inválido.",
                  (val) => !val || val.length !== 44 || validateDigitoVerificadorChaveAcesso(val)
                ),
              tipoDocumento: yup
                .number()
                .required("Tipo de documento é obrigatório")
                .oneOf([1, 2], "Tipo de documento deve ser 1 (CT-e) ou 2 (NF-e)"),
            })
          )
          .required("Cada município de descarga deve conter pelo menos um documento fiscal (CT-e ou NF-e).")
          .min(1, "Cada município de descarga deve conter pelo menos um documento fiscal (CT-e ou NF-e)."),
      })
    )
    .required("Deve haver pelo menos um município de descarregamento.")
    .min(1, "Deve haver pelo menos um município de descarregamento.")
    .test(
      "rejeicao-612-municipios-descarga",
      function (municipios) {
        const ufFim = this.parent?.ufFim;
        if (!ufFim || !municipios || !Array.isArray(municipios)) return true;
        for (let i = 0; i < municipios.length; i++) {
          const cod = municipios[i]?.codigoMunicipioDescarga;
          if (cod && !validarMunicipioPertenceUf(cod, ufFim)) {
            return this.createError({
              path: `${this.path}[${i}].codigoMunicipioDescarga`,
              message: `Código de Município (${cod}) diverge da UF de descarga do MDF-e (${ufFim}) (Rejeição 612).`,
            });
          }
        }
        return true;
      }
    ),

  condutores: yup
    .array()
    .of(
      yup.object().shape({
        nomeCondutor: yup
          .string()
          .required("Nome do condutor é obrigatório.")
          .min(2, "Nome do condutor deve ter entre 2 e 60 caracteres.")
          .max(60, "Nome do condutor deve ter entre 2 e 60 caracteres."),
        cpfCondutor: yup
          .string()
          .required("CPF do condutor é obrigatório.")
          .transform((v) => (v ? v.replace(/\D/g, "") : ""))
          .test(
            "cpf-condutor-valido",
            "CPF do condutor é inválido.",
            (v) => Boolean(v && validateCpf(v))
          ),
      })
    )
    .required("Deve haver pelo menos um condutor informado.")
    .min(1, "Deve haver pelo menos um condutor informado."),

  percursos: yup
    .array()
    .of(
      yup.object().shape({
        ufPercurso: yup
          .string()
          .required("UF do percurso é obrigatória.")
          .test(
            "uf-percurso-tamanho",
            "UF do percurso deve conter 2 caracteres.",
            (val) => !val || val.trim().length === 2
          )
          .test(
            "uf-percurso-valida",
            "UF do percurso é inválida.",
            (val) => Boolean(val && UFS_VALIDAS_MDFE.includes(val.trim().toUpperCase()))
          ),
      })
    )
    .notRequired(),

  seguros: yup
    .array()
    .of(
      yup.object().shape({
        responsavelSeguro: yup
          .number()
          .required("Responsável pelo seguro deve ser válido (1 = Emitente do MDF-e, 2 = Responsável pela contratação do serviço).")
          .oneOf([1, 2], "Responsável pelo seguro deve ser válido (1 = Emitente do MDF-e, 2 = Responsável pela contratação do serviço)."),
        documentoResponsavel: yup
          .string()
          .nullable()
          .notRequired()
          .test(
            "doc-resp-seguro-obrigatorio-contratante",
            "Documento (CPF ou CNPJ) do responsável pelo seguro é obrigatório quando o contratante for o responsável (Rejeição 699).",
            function (v) {
              const resp = Number(this.parent?.responsavelSeguro);
              if (resp === 2) {
                return Boolean(v && v.trim() !== "");
              }
              return true;
            }
          )
          .test(
            "doc-resp-seguro-valido",
            "Documento do responsável pelo seguro é inválido (deve ser um CPF ou CNPJ válido).",
            (v) => {
              if (!v || v.trim() === "") return true;
              return validateCpfOuCnpj(v);
            }
          ),
        nomeSeguradora: yup
          .string()
          .required("Nome da seguradora é obrigatório (Rejeição 699).")
          .min(2, "Nome da seguradora deve conter entre 2 e 30 caracteres conforme manual SEFAZ.")
          .max(30, "Nome da seguradora deve conter no máximo 30 caracteres conforme manual SEFAZ."),
        cnpjSeguradora: yup
          .string()
          .required("CNPJ da seguradora é obrigatório (Rejeição 699).")
          .transform((v) => (v ? v.replace(/[^A-Za-z0-9]/g, "").toUpperCase() : ""))
          .test(
            "cnpj-seguradora-valido",
            "CNPJ da seguradora é inválido.",
            (v) => Boolean(v && validateCnpj(v))
          ),
        numeroApolice: yup
          .string()
          .required("Número da apólice do seguro é obrigatório para o modal rodoviário (Rejeição 699).")
          .max(20, "Número da apólice deve conter no máximo 20 caracteres conforme manual SEFAZ."),
        numeroAverbaCorrente: yup
          .string()
          .required("Número de averbação do seguro é obrigatório para o modal rodoviário (Rejeição 699).")
          .max(40, "Número de averbação deve conter no máximo 40 caracteres conforme manual SEFAZ."),
      })
    )
    .notRequired(),

  produtoPredominante: yup
    .object()
    .shape({
      tipoCarga: yup
        .number()
        .required("Tipo de carga da ANTT deve ser válido.")
        .min(1, "Tipo de carga da ANTT deve ser válido.")
        .max(11, "Tipo de carga da ANTT deve ser válido."),
      nomeProduto: yup
        .string()
        .required("Nome do produto predominante é obrigatório.")
        .min(2, "Nome do produto predominante deve ter entre 2 e 120 caracteres conforme manual SEFAZ.")
        .max(120, "Nome do produto predominante deve ter entre 2 e 120 caracteres conforme manual SEFAZ."),
      ncm: yup
        .string()
        .nullable()
        .notRequired()
        .test(
          "rejeicao-301-ncm",
          "O NCM do produto predominante da carga lotação deve ser informado (Rejeição 301).",
          function (v) {
            const cepCarreg = this.parent?.cepCarregamento;
            const cepDesc = this.parent?.cepDescarregamento;
            const temCep = Boolean(
              (cepCarreg && cepCarreg.trim() !== "") ||
              (cepDesc && cepDesc.trim() !== "")
            );
            if (temCep) {
              return Boolean(v && v.trim() !== "");
            }
            return true;
          }
        )
        .test(
          "ncm-valido",
          "NCM deve conter exatamente 8 dígitos numéricos.",
          (v) => {
            if (!v || v.trim() === "") return true;
            return v.replace(/\D/g, "").length === 8;
          }
        )
        .test(
          "ncm-apenas-numeros",
          "NCM deve conter apenas números.",
          (v) => {
            if (!v || v.trim() === "") return true;
            return /^\d+$/.test(v.trim());
          }
        ),
      codigoEAN: yup
        .string()
        .nullable()
        .notRequired()
        .test(
          "codigo-ean-valido",
          "O código de barras deve ser GTIN-8, GTIN-12, GTIN-13, GTIN-14 ou 'SEM GTIN'.",
          (v) => {
            if (!v || v.trim() === "") return true;
            const trimmed = v.trim().toUpperCase();
            if (trimmed === "SEM GTIN") return true;
            const limpo = v.replace(/\D/g, "");
            return [8, 12, 13, 14].includes(limpo.length);
          }
        ),
      cepCarregamento: yup
        .string()
        .nullable()
        .notRequired()
        .test(
          "cep-c-codependente",
          "Quando informado o CEP de descarregamento, o CEP de carregamento também deve ser informado.",
          function (v) {
            const cepDesc = this.parent?.cepDescarregamento;
            if (cepDesc && cepDesc.trim() !== "") {
              return Boolean(v && v.trim() !== "");
            }
            return true;
          }
        )
        .test(
          "cep-c-valido",
          "CEP de carregamento deve conter 8 dígitos numéricos.",
          (v) => {
            if (!v || v.trim() === "") return true;
            const limpo = v.replace(/\D/g, "");
            return limpo.length === 8;
          }
        ),
      cepDescarregamento: yup
        .string()
        .nullable()
        .notRequired()
        .test(
          "cep-d-codependente",
          "Quando informado o CEP de carregamento, o CEP de descarregamento também deve ser informado.",
          function (v) {
            const cepCarreg = this.parent?.cepCarregamento;
            if (cepCarreg && cepCarreg.trim() !== "") {
              return Boolean(v && v.trim() !== "");
            }
            return true;
          }
        )
        .test(
          "cep-d-valido",
          "CEP de descarregamento deve conter 8 dígitos numéricos.",
          (v) => {
            if (!v || v.trim() === "") return true;
            const limpo = v.replace(/\D/g, "");
            return limpo.length === 8;
          }
        ),
    })
    .default(undefined)
    .nullable()
    .notRequired(),

  ciots: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          ciot: yup
            .string()
            .required("CIOT é obrigatório.")
            .test(
              "ciot-12",
              "CIOT deve conter exatamente 12 dígitos numéricos conforme ANTT.",
              (v) => Boolean(v && v.replace(/\D/g, "").length === 12)
            )
            .test(
              "ciot-apenas-numeros",
              "CIOT deve conter apenas números.",
              (v) => !v || /^\d+$/.test(v.replace(/\D/g, ""))
            ),
          documento: yup
            .string()
            .nullable()
            .notRequired()
            .transform((v) => (v ? v.replace(/[.\-/\s]/g, "").toUpperCase() : ""))
            .test(
              "doc-ciot-valido",
              "Documento (CPF ou CNPJ) do responsável pelo CIOT é inválido.",
              (v) => !v || validateCpfOuCnpj(v)
            ),
          cpf: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cpf-ciot-valido",
              "CPF do responsável pelo CIOT é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCpf(v);
              }
            ),
          cnpj: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cnpj-ciot-valido",
              "CNPJ do responsável pelo CIOT é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCnpj(v);
              }
            ),
        })
        .test(
          "ciot-doc-obrigatorio",
          "CPF ou CNPJ do responsável pela geração do CIOT é obrigatório.",
          function (c) {
            if (!c) return true;
            return Boolean(
              (c.documento && c.documento.trim() !== "") ||
              (c.cpf && c.cpf.trim() !== "") ||
              (c.cnpj && c.cnpj.trim() !== "")
            );
          }
        )
    )
    .notRequired(),

  valesPedagio: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          nomeFornecedoraValePedagio: yup.string().nullable().notRequired(),
          numeroComprovante: yup
            .string()
            .required("Número do comprovante de compra do Vale-Pedágio é obrigatório.")
            .max(20, "Número do comprovante deve conter no máximo 20 caracteres conforme manual SEFAZ."),
          valorValePedagio: yup
            .number()
            .required("Valor do Vale-Pedágio deve ser maior que zero.")
            .typeError("Valor do Vale-Pedágio deve ser numérico.")
            .moreThan(0, "Valor do Vale-Pedágio deve ser maior que zero."),
          cnpjEmitenteValePedagio: yup
            .string()
            .required("CNPJ da empresa fornecedora do Vale-Pedágio (CNPJForn) é obrigatório.")
            .test(
              "cnpj-emitente-pedagio-valido",
              "CNPJ da fornecedora do Vale-Pedágio é inválido.",
              (v) => Boolean(v && validateCnpj(v))
            ),
          documentoResponsavelPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .transform((v) => (v ? v.replace(/[.\-/\s]/g, "").toUpperCase() : ""))
            .test(
              "doc-resp-pedagio-valido",
              "Documento (CPF ou CNPJ) do responsável pelo pagamento do Vale-Pedágio é inválido.",
              (v) => !v || validateCpfOuCnpj(v)
            ),
          nomeResponsavelPagamento: yup.string().nullable().notRequired(),
          cnpjResponsavelPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cnpj-resp-pedagio-valido",
              "CNPJ do responsável pelo pagamento do Vale-Pedágio é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCnpj(v);
              }
            ),
          cpfResponsavelPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cpf-resp-pedagio-valido",
              "CPF do responsável pelo pagamento do Vale-Pedágio é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCpf(v);
              }
            ),
          tipoValePedagio: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "tipo-vale-pedagio",
              "Tipo de Vale-Pedágio deve ser 01 (Tag eletrônica), 02 (Cupom/Voucher) ou 03 (Cartão magnético).",
              (v) => !v || ["01", "02", "03"].includes(v)
            ),
          categoriaCombinacaoVeicular: yup
            .string()
            .required(
              "A categoria de combinação veicular deve ser preenchida para o grupo vale pedágio (Rejeição 731)."
            )
            .matches(
              /^(02|04|06|07|08|10|11|12|13|14)$/,
              "Categoria de combinação veicular inválida (Rejeição 731). Valores aceitos conforme ANTT: 02, 04, 06, 07, 08, 10, 11, 12, 13 ou 14."
            ),
        })
    )
    .notRequired(),

  contratantes: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          nomeContratante: yup
            .string()
            .nullable()
            .notRequired()
            .max(60, "Nome do contratante deve conter no máximo 60 caracteres."),
          documento: yup
            .string()
            .nullable()
            .notRequired()
            .transform((v) => (v ? v.replace(/[.\-/\s]/g, "").toUpperCase() : ""))
            .test(
              "doc-contratante-valido",
              "Documento (CPF ou CNPJ) do contratante é inválido.",
              (v) => !v || validateCpfOuCnpj(v)
            ),
          cpf: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cpf-contratante-valido",
              "CPF do contratante é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCpf(v);
              }
            ),
          cnpj: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cnpj-contratante-valido",
              "CNPJ do contratante é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCnpj(v);
              }
            ),
          idEstrangeiro: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "id-estrangeiro-contratante-len",
              "Identificador estrangeiro do contratante deve ter entre 2 e 20 caracteres.",
              (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 20)
            ),
        })
        .test(
          "contratante-doc-obrigatorio",
          "Identificação do contratante é obrigatória (informe CPF, CNPJ ou Identificação Estrangeira).",
          function (c) {
            if (!c) return true;
            return Boolean(
              (c.documento && c.documento.trim() !== "") ||
              (c.cpf && c.cpf.trim() !== "") ||
              (c.cnpj && c.cnpj.trim() !== "") ||
              (c.idEstrangeiro && c.idEstrangeiro.trim() !== "")
            );
          }
        )
        .test(
          "contratante-apenas-um-doc",
          "Informe apenas um documento para o contratante (CPF/CNPJ ou Identificação Estrangeira).",
          function (c) {
            if (!c) return true;
            const hasDoc = Boolean(
              (c.documento && c.documento.trim() !== "") ||
              (c.cpf && c.cpf.trim() !== "") ||
              (c.cnpj && c.cnpj.trim() !== "")
            );
            const hasIdEst = Boolean(c.idEstrangeiro && c.idEstrangeiro.trim() !== "");
            return !(hasDoc && hasIdEst);
          }
        )
    )
    .notRequired(),

  pagamentos: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          nomeBeneficiario: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "nome-beneficiario-len",
              "Nome do responsável pelo pagamento deve ter entre 2 e 60 caracteres.",
              (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 60)
            ),
          documento: yup
            .string()
            .nullable()
            .notRequired()
            .transform((v) => (v ? v.replace(/[.\-/\s]/g, "").toUpperCase() : ""))
            .test(
              "doc-pagamento-valido",
              "Documento (CPF ou CNPJ) do beneficiário/responsável é inválido.",
              (v) => !v || validateCpfOuCnpj(v)
            ),
          cpfBeneficiario: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cpf-beneficiario-valido",
              "CPF do responsável pelo pagamento é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCpf(v);
              }
            ),
          cnpjBeneficiario: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cnpj-beneficiario-valido",
              "CNPJ do responsável pelo pagamento é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCnpj(v);
              }
            ),
          idEstrangeiroBeneficiario: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "id-estrangeiro-beneficiario-len",
              "Identificador estrangeiro do responsável pelo pagamento deve ter entre 2 e 20 caracteres.",
              (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 20)
            ),
          valorTotalContrato: yup
            .number()
            .required("O valor total do contrato deve ser maior que zero.")
            .typeError("O valor total do contrato deve ser maior que zero.")
            .moreThan(0, "O valor total do contrato deve ser maior que zero."),
          formaPagamento: yup
            .number()
            .required("Forma de pagamento do frete deve ser válida (0 = À Vista, 1 = A Prazo).")
            .oneOf([0, 1], "Forma de pagamento do frete deve ser válida (0 = À Vista, 1 = A Prazo)."),
          documentoResponsavelPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .transform((v) => (v ? v.replace(/[.\-/\s]/g, "").toUpperCase() : ""))
            .test(
              "doc-resp-pagamento-valido",
              "Documento (CPF ou CNPJ) do responsável pelo pagamento é inválido.",
              (v) => !v || validateCpfOuCnpj(v)
            ),
          cnpjResponsavelPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cnpj-resp-pagamento-valido",
              "CNPJ do responsável pelo pagamento é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCnpj(v);
              }
            ),
          cpfResponsavelPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cpf-resp-pagamento-valido",
              "CPF do responsável pelo pagamento é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCpf(v);
              }
            ),
          idEstrangeiroResponsavelPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "id-estrangeiro-resp-len",
              "Identificador estrangeiro do responsável pelo pagamento deve ter entre 2 e 20 caracteres.",
              (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 20)
            ),
          cnpjInstituicaoPagamento: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "cnpj-ipef-valido",
              "CNPJ da Instituição de Pagamento Eletrônico do Frete (CNPJIPEF) é inválido.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return validateCnpj(v);
              }
            ),
          chavePixBancario: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "pix-comprimento",
              "Chave PIX deve ter entre 2 e 60 caracteres.",
              (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 60)
            ),
          valorAdiantamento: yup
            .number()
            .nullable()
            .notRequired()
            .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
            .min(0, "Valor do adiantamento deve ser maior ou igual a zero.")
            .test(
              "rejeicao-739-adiantamento-a-vista",
              "Valor do adiantamento não pode ser informado para pagamento a vista (Rejeição 739).",
              function (v) {
                if (!v || v === 0) return true;
                const formaPag = Number(this.parent?.formaPagamento);
                if (formaPag === 0) {
                  return false;
                }
                return true;
              }
            )
            .test(
              "rejeicao-740-adiantamento-menor-contrato",
              "Valor do adiantamento deve ser menor que o valor total do contrato (Rejeição 740).",
              function (v) {
                if (!v || v === 0) return true;
                const formaPag = Number(this.parent?.formaPagamento);
                const valorTotal = Number(this.parent?.valorTotalContrato) || 0;
                if (formaPag === 1 && v >= valorTotal) {
                  return false;
                }
                return true;
              }
            ),
          tipoAntecipacao: yup
            .number()
            .nullable()
            .notRequired()
            .transform((val, orig) => (orig === "" || isNaN(val) ? null : val))
            .test(
              "tipo-antecipacao-valido",
              "Tipo de antecipação deve ser válido (0 = Não permite, 1 = Permite, 2 = Permite mediante confirmação).",
              (v) => v === null || v === undefined || (v >= 0 && v <= 2)
            ),
          codigoBanco: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "codigo-banco-req-se-agencia",
              "Código do banco é obrigatório ao informar dados bancários da conta.",
              function (v) {
                const ag = this.parent?.codigoAgencia;
                if (ag && ag.trim() !== "") {
                  return Boolean(v && v.trim() !== "");
                }
                return true;
              }
            )
            .test(
              "codigo-banco-tamanho",
              "Código do banco deve ter entre 3 e 5 dígitos.",
              (v) => {
                if (!v || v.trim() === "") return true;
                const limpo = v.replace(/\D/g, "");
                return limpo.length >= 3 && limpo.length <= 5;
              }
            )
            .test(
              "codigo-banco-numeros",
              "Código do banco deve conter apenas dígitos numéricos.",
              (v) => {
                if (!v || v.trim() === "") return true;
                return /^\d+$/.test(v.trim());
              }
            ),
          codigoAgencia: yup
            .string()
            .nullable()
            .notRequired()
            .test(
              "codigo-agencia-req-se-banco",
              "Código da agência é obrigatório ao informar dados bancários da conta.",
              function (v) {
                const b = this.parent?.codigoBanco;
                if (b && b.trim() !== "") {
                  return Boolean(v && v.trim() !== "");
                }
                return true;
              }
            )
            .test(
              "codigo-agencia-tamanho",
              "Código da agência deve ter entre 1 e 10 dígitos.",
              (v) => {
                if (!v || v.trim() === "") return true;
                const limpo = v.replace(/\D/g, "");
                return limpo.length >= 1 && limpo.length <= 10;
              }
            ),
          componentes: yup
            .array()
            .of(
              yup.object().shape({
                tipoComponente: yup
                  .number()
                  .required("Tipo do componente de pagamento do frete deve ser válido.")
                  .oneOf([1, 2, 3, 4, 99], "Tipo do componente de pagamento do frete deve ser válido."),
                valorComponente: yup
                  .number()
                  .required("Valor do componente deve ser maior que zero.")
                  .typeError("Valor do componente deve ser maior que zero.")
                  .moreThan(0, "Valor do componente deve ser maior que zero."),
                descricaoOutro: yup.string().when("tipoComponente", {
                  is: (val: number) => Number(val) === 99,
                  then: (schema) =>
                    schema
                      .required("Descrição do componente (xComp) é obrigatória quando o tipo for 'Outros'.")
                      .min(2, "Descrição do componente deve ter entre 2 e 60 caracteres.")
                      .max(60, "Descrição do componente deve ter entre 2 e 60 caracteres."),
                  otherwise: (schema) =>
                    schema
                      .nullable()
                      .notRequired()
                      .test(
                        "desc-outro-len",
                        "Descrição do componente deve ter entre 2 e 60 caracteres.",
                        (v) => !v || v.trim() === "" || (v.trim().length >= 2 && v.trim().length <= 60)
                      ),
                }),
              })
            )
            .required("Deve haver pelo menos um componente da tarifa do frete.")
            .min(1, "Deve haver pelo menos um componente da tarifa do frete."),
          parcelas: yup
            .array()
            .of(
              yup.object().shape({
                numeroParcela: yup
                  .number()
                  .required("Número da parcela deve estar entre 1 e 999.")
                  .typeError("Número da parcela deve estar entre 1 e 999.")
                  .min(1, "Número da parcela deve estar entre 1 e 999.")
                  .max(999, "Número da parcela deve estar entre 1 e 999."),
                dataVencimento: yup
                  .string()
                  .required("Data de vencimento da parcela é obrigatória e deve ser válida.")
                  .test(
                    "data-venc-valida",
                    "Data de vencimento da parcela é obrigatória e deve ser válida.",
                    (v) => Boolean(v && moment(v, ["YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601]).isValid())
                  ),
                valorParcela: yup
                  .number()
                  .required("Valor da parcela deve ser maior que zero.")
                  .typeError("Valor da parcela deve ser maior que zero.")
                  .moreThan(0, "Valor da parcela deve ser maior que zero."),
              })
            )
            .when("formaPagamento", {
              is: (val: number) => Number(val) === 1,
              then: (schema) =>
                schema
                  .required("Informações de parcelas (infPrazo) são obrigatórias para pagamento a prazo.")
                  .min(1, "Informações de parcelas (infPrazo) são obrigatórias para pagamento a prazo."),
              otherwise: (schema) => schema.notRequired(),
            }),
        })
        .test(
          "pagamento-id-responsavel",
          "Identificação do responsável pelo pagamento é obrigatória (informe CPF, CNPJ ou Identificação Estrangeira).",
          function (p) {
            if (!p) return true;
            const hasId = Boolean(
              (p.documento && p.documento.trim() !== "") ||
              (p.cpfBeneficiario && p.cpfBeneficiario.trim() !== "") ||
              (p.cnpjBeneficiario && p.cnpjBeneficiario.trim() !== "") ||
              (p.idEstrangeiroBeneficiario && p.idEstrangeiroBeneficiario.trim() !== "") ||
              (p.cpfResponsavelPagamento && p.cpfResponsavelPagamento.trim() !== "") ||
              (p.cnpjResponsavelPagamento && p.cnpjResponsavelPagamento.trim() !== "") ||
              (p.idEstrangeiroResponsavelPagamento && p.idEstrangeiroResponsavelPagamento.trim() !== "")
            );
            if (!hasId) {
              return this.createError({
                path: `${this.path}.documento`,
                message: "Identificação do responsável pelo pagamento é obrigatória (informe CPF, CNPJ ou Identificação Estrangeira).",
              });
            }
            return true;
          }
        )
        .test(
          "pagamento-infbanc-obrigatorio",
          "Informações bancárias de recebimento do frete (infBanc) são obrigatórias. Informe uma das opções: dados bancários (banco e agência), CNPJ da IPEF ou Chave PIX.",
          function (p) {
            if (!p) return true;
            const temBanc = Boolean(
              (p.codigoBanco && p.codigoBanco.trim() !== "") ||
              (p.codigoAgencia && p.codigoAgencia.trim() !== "")
            );
            const temIpef = Boolean(p.cnpjInstituicaoPagamento && p.cnpjInstituicaoPagamento.trim() !== "");
            const temPix = Boolean(p.chavePixBancario && p.chavePixBancario.trim() !== "");
            if (!temBanc && !temIpef && !temPix) {
              return this.createError({
                path: `${this.path}.chavePixBancario`,
                message: "Informações bancárias de recebimento do frete (infBanc) são obrigatórias. Informe uma das opções: dados bancários (banco e agência), CNPJ da IPEF ou Chave PIX.",
              });
            }
            return true;
          }
        )
        .test(
          "pagamento-infbanc-mutex",
          "Dados bancários (banco/agência), CNPJ da IPEF e Chave PIX são mutuamente exclusivos no grupo infBanc — informe apenas uma das modalidades.",
          function (p) {
            if (!p) return true;
            const temBanc = Boolean(
              (p.codigoBanco && p.codigoBanco.trim() !== "") ||
              (p.codigoAgencia && p.codigoAgencia.trim() !== "")
            );
            const temIpef = Boolean(p.cnpjInstituicaoPagamento && p.cnpjInstituicaoPagamento.trim() !== "");
            const temPix = Boolean(p.chavePixBancario && p.chavePixBancario.trim() !== "");
            let count = 0;
            if (temBanc) count++;
            if (temIpef) count++;
            if (temPix) count++;
            return count <= 1;
          }
        )
    )
    .notRequired(),
})
  .test(
    "rejeicao-578",
    "Informações dos tomadores é obrigatória para esta operação (Rejeição 578). Informe ao menos um Contratante (infContratante), CIOT (infCIOT) ou Vale-Pedágio (valePed).",
    function (input: any) {
      if (!input) return true;
      const isRodoviario = Number(input.modal) === 1;
      const tipoEmit = Number(input.tipoEmitente);
      const tipoTransp = input.tipoTransportador;
      const exigeTomador =
        isRodoviario &&
        (tipoEmit === 1 ||
          tipoEmit === 3 ||
          (tipoEmit === 2 && Boolean(tipoTransp)));

      if (!exigeTomador) return true;

      const hasContratante = Boolean(input.contratantes && input.contratantes.length > 0);
      const hasCiot = Boolean(input.ciots && input.ciots.length > 0);
      const hasValePedagio = Boolean(input.valesPedagio && input.valesPedagio.length > 0);

      if (!hasContratante && !hasCiot && !hasValePedagio) {
        return this.createError({
          path: "contratantes",
          message:
            "Informações dos tomadores é obrigatória para esta operação (Rejeição 578). Informe ao menos um Contratante (infContratante), CIOT (infCIOT) ou Vale-Pedágio (valePed).",
        });
      }
      return true;
    }
  )
  .test(
    "rejeicao-726",
    "O grupo de informações da carga lotação deve ser informado (Rejeição 726). Para operação com documento único (carga lotação), informe os dados do produto predominante com os locais de carregamento e descarregamento.",
    function (input: any) {
      if (!input) return true;
      const isRodoviario = Number(input.modal) === 1;
      const tipoEmit = Number(input.tipoEmitente);
      const isPrestador = tipoEmit === 1 || tipoEmit === 3;

      if (!isRodoviario || !isPrestador) return true;

      const totalDocumentos = (input.municipiosDescarga || []).reduce(
        (acc: number, m: any) => acc + (m.documentos ? m.documentos.length : 0),
        0
      );

      if (totalDocumentos !== 1) return true;

      const prod = input.produtoPredominante;
      if (!prod || !prod.nomeProduto || prod.nomeProduto.trim() === "") {
        return this.createError({
          path: "produtoPredominante",
          message:
            "O grupo de informações da carga lotação deve ser informado (Rejeição 726). Para operação com documento único (carga lotação), informe os dados do produto predominante com os locais de carregamento e descarregamento.",
        });
      }

      if (!prod.cepCarregamento || prod.cepCarregamento.trim() === "") {
        return this.createError({
          path: "produtoPredominante.cepCarregamento",
          message:
            "O grupo de informações da carga lotação deve ser informado (Rejeição 726). CEP de carregamento é obrigatório para carga lotação.",
        });
      }

      if (!prod.cepDescarregamento || prod.cepDescarregamento.trim() === "") {
        return this.createError({
          path: "produtoPredominante.cepDescarregamento",
          message:
            "O grupo de informações da carga lotação deve ser informado (Rejeição 726). CEP de descarregamento é obrigatório para carga lotação.",
        });
      }

      return true;
    }
  )
  .test(
    "rejeicao-301",
    "O NCM do produto predominante da carga lotação deve ser informado (Rejeição 301).",
    function (input: any) {
      if (!input) return true;
      const isRodoviario = Number(input.modal) === 1;
      const tipoEmit = Number(input.tipoEmitente);
      const isCargaPropriaComTransp =
        tipoEmit === 2 &&
        input.tipoTransportador !== null &&
        input.tipoTransportador !== undefined &&
        input.tipoTransportador !== "";
      const exigeCargaLotacao =
        isRodoviario &&
        (tipoEmit === 1 || tipoEmit === 3 || isCargaPropriaComTransp);

      if (!exigeCargaLotacao) return true;

      const totalDocumentos = (input.municipiosDescarga || []).reduce(
        (acc: number, m: any) => acc + (m.documentos ? m.documentos.length : 0),
        0
      );

      const prod = input.produtoPredominante;
      const temCep = Boolean(
        prod &&
          ((prod.cepCarregamento && prod.cepCarregamento.trim() !== "") ||
            (prod.cepDescarregamento && prod.cepDescarregamento.trim() !== ""))
      );

      if (totalDocumentos === 1 || temCep) {
        if (!prod || !prod.ncm || prod.ncm.trim() === "") {
          return this.createError({
            path: "produtoPredominante.ncm",
            message:
              "O NCM do produto predominante da carga lotação deve ser informado (Rejeição 301).",
          });
        }
        const limpo = prod.ncm.replace(/\D/g, "");
        if (limpo.length !== 8) {
          return this.createError({
            path: "produtoPredominante.ncm",
            message:
              "NCM do produto predominante deve conter exatamente 8 dígitos numéricos.",
          });
        }
      }

      return true;
    }
  );

export type MDFeInputFormType = yup.InferType<typeof MDFeInputSchema>;

/**
 * Validador executado na finalização prévia à inclusão do MDF-e.
 * Retorna se o objeto final está válido e a lista de mensagens de erro amigáveis
 * caso falhe em alguma das regras da SEFAZ/FluentValidation.
 */
export async function validarMDFeInclusao(
  payload: any
): Promise<{ isValid: boolean; errors: string[] }> {
  try {
    await MDFeInputSchema.validate(payload, { abortEarly: false });
    return { isValid: true, errors: [] };
  } catch (err: any) {
    const rawErrors: string[] =
      err?.inner && err.inner.length > 0
        ? err.inner.map((e: any) => e.message)
        : [err?.message || "Existem inconsistências nos dados do MDF-e."];
    const uniqueErrors = Array.from(new Set(rawErrors));
    return { isValid: false, errors: uniqueErrors };
  }
}
